/**
 * The live viewer page — a teammate's read-only window onto someone else's
 * running Tavsin session (Team tier, hosted web only). Reached at
 * `/#live=<token>`: the token rides in the fragment so the browser never sends
 * it to a server log; the page hands it to `/api/live/watch` in a POST body.
 *
 * Framework-free like app.ts. It renders what the server says happened —
 * history, the live turn's text, tool calls, files touched, the verification
 * verdict — and, only for a steer-scoped share, a box to course-correct the
 * running turn. There is deliberately nothing here that could approve an
 * action on the owner's behalf.
 */
import { AuthError, type LiveViewEvent, steerLiveSession, type TurnVerificationReply, watchLiveSession } from "./api.js";
import { el, formatBody, toast } from "./dom.js";

/** The share token from `#live=<token>`, or null. */
export function liveTokenFromHash(hash: string = window.location.hash): string | null {
  const m = /^#live=(tvlive_[A-Za-z0-9_-]{20,120})$/.exec(hash);
  return m ? m[1]! : null;
}

function verificationLine(v: TurnVerificationReply | null): string {
  if (!v) return "Finished — no checks ran.";
  const last = v.checks[v.checks.length - 1];
  const label = { verified: "Verified ✓", failing: "Checks failing ✗", unverified: "Changes unverified", ran: "Ran" }[v.status];
  return last ? `${label} · ${last.command} (exit ${String(last.exitCode)})` : label;
}

const END_TEXT: Record<string, string> = {
  revoked: "The owner stopped sharing this session.",
  expired: "This share link expired.",
  access: "You no longer have access to this session.",
  slow: "Disconnected: the connection fell too far behind. Reload to rejoin.",
  shutdown: "The server restarted. Reload to rejoin.",
};

export function mountLiveView(root: HTMLElement, token: string, onAuthRequired: () => void): void {
  const title = el("div", { class: "live-title" }, ["Connecting to the live session…"]);
  const meta = el("div", { class: "live-meta" }, []);
  const status = el("div", { class: "live-status" }, ["connecting"]);
  const transcript = el("div", { class: "live-transcript" }, []);
  const footer = el("div", { class: "live-footer" }, []);
  root.replaceChildren(el("div", { class: "live-view" }, [el("header", { class: "live-head" }, [title, meta, status]), transcript, footer]));

  let liveBody: HTMLElement | null = null;
  let liveText = "";
  let activity: HTMLElement | null = null;
  let ended = false;

  const scroll = (): void => { transcript.scrollTop = transcript.scrollHeight; };
  const addTurn = (role: "user" | "assistant", content: string, who?: string): HTMLElement => {
    const body = el("div", { class: "turn-body", html: formatBody(content) });
    const label = role === "user" ? (who ?? "Owner") : "Tavsin";
    transcript.append(el("div", { class: `turn ${role === "user" ? "turn-user" : "turn-assistant"}` }, [
      el("div", { class: "turn-header" }, [el("div", { class: "turn-role" }, [label])]),
      body,
    ]));
    scroll();
    return body;
  };
  const addActivity = (line: string): void => {
    if (!activity) {
      activity = el("ul", { class: "live-activity" }, []);
      transcript.append(activity);
    }
    activity.append(el("li", {}, [line]));
    scroll();
  };

  const onEvent = (e: LiveViewEvent): void => {
    switch (e.t) {
      case "hello": {
        title.textContent = e.share.title;
        meta.textContent = `${e.share.owner}'s session · ${e.share.scope === "steer" ? "you can steer" : "view only"} · until ${new Date(e.share.expiresAt).toLocaleString()}`;
        status.textContent = "idle — waiting for the next turn";
        for (const m of e.history) addTurn(m.role === "user" ? "user" : "assistant", m.content, m.meta === "sent mid-turn" ? "Steer" : undefined);
        if (e.share.scope === "steer") footer.replaceChildren(steerBox(token));
        break;
      }
      case "turn":
        addTurn("user", e.message);
        activity = null;
        liveText = "";
        liveBody = addTurn("assistant", "…");
        status.textContent = "live — the agent is working";
        break;
      case "delta":
        liveText += e.text;
        if (liveBody) liveBody.innerHTML = formatBody(liveText);
        scroll();
        break;
      case "tool":
        addActivity(`${e.status === "running" ? "▸" : e.status === "done" ? "✓" : e.status === "blocked" ? "⛔" : "✗"} ${e.tool}${e.detail ? ` — ${e.detail.slice(0, 160)}` : ""}`);
        break;
      case "file":
        addActivity(`✎ ${e.change} ${e.path}`);
        break;
      case "steer":
        addTurn("user", e.text, `Steer from ${e.by}`);
        break;
      case "truncated":
        addActivity("(earlier output from this turn was trimmed)");
        break;
      case "done":
        status.textContent = "idle — waiting for the next turn";
        addActivity(verificationLine(e.verification));
        liveBody = null;
        break;
      case "error":
        status.textContent = "the turn ended with an error";
        liveBody = null;
        break;
      case "end":
        ended = true;
        status.textContent = END_TEXT[e.reason] ?? "The live session ended.";
        footer.replaceChildren();
        break;
    }
  };

  const { done } = watchLiveSession(token, onEvent);
  done
    .then(() => { if (!ended) status.textContent = "Disconnected. Reload to rejoin."; })
    .catch((err: unknown) => {
      if (err instanceof AuthError) { onAuthRequired(); return; }
      title.textContent = "Can't open this live session";
      status.textContent = err instanceof Error ? err.message : String(err);
    });
}

function steerBox(token: string): HTMLElement {
  const input = el("textarea", { class: "live-steer-input", rows: "2", placeholder: "Steer the running turn — the agent sees it as a note from you, not from the owner" }) as HTMLTextAreaElement;
  const send = el("button", { class: "btn btn-primary btn-sm" }, ["Steer"]) as HTMLButtonElement;
  send.onclick = async () => {
    const text = input.value.trim();
    if (!text) return;
    send.disabled = true;
    try {
      const accepted = await steerLiveSession(token, text);
      if (accepted) { input.value = ""; toast("Sent to the running turn", "success"); }
      else toast("Nothing is running right now — wait for the owner's next turn", "info");
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      send.disabled = false;
    }
  };
  return el("div", { class: "live-steer" }, [input, send]);
}
